/**
 * The landing's code colours, as the approved mock draws them: a small highlighter whose colours are the
 * teammates' inks (t-k keywords, t-s strings, t-f calls, t-c comments, t-n numbers, t-y keys and flags), then
 * the traveller and their words picked out wherever they appear (mark.u, mark.m). It runs when the page is
 * built, so the browser gets spans, never the highlighter.
 */
export type Lang = "ts" | "py" | "sh" | "yaml" | "json";
export type Colour = "k" | "s" | "f" | "c" | "n" | "y";

export interface Token {
  text: string;
  colour?: Colour;
  mark?: "u" | "m";
}

const RULES: Record<Lang, ReadonlyArray<readonly [Colour, RegExp]>> = {
  ts: [
    ["c", /\/\/[^\n]*/y],
    ["s", /'(?:[^'\\\n]|\\.)*'|"(?:[^"\\\n]|\\.)*"/y],
    [
      "k",
      /\b(?:import|from|const|let|await|async|for|of|if|continue|return|new|export|function)\b/y,
    ],
    ["f", /(?<=\.)[A-Za-z_]\w*(?=\()/y],
    ["n", /\b\d[\d.]*\b/y],
  ],
  py: [
    ["c", /#[^\n]*/y],
    ["s", /'(?:[^'\\\n]|\\.)*'|"(?:[^"\\\n]|\\.)*"/y],
    ["k", /\b(?:from|import|for|in|if|return|def|async|await)\b/y],
    ["f", /(?<=\.)[A-Za-z_]\w*(?=\()|\bprint\b/y],
    ["n", /\b\d[\d.]*\b/y],
  ],
  sh: [
    ["c", /(?<![^\s])#[^\n]*/y],
    ["s", /'[^']*'|"(?:[^"\\]|\\.)*"/y],
    ["y", /(?<=\s)--?[A-Za-z][\w-]*/y],
    ["k", /\b(?:curl|vestra)\b/y],
  ],
  yaml: [
    ["c", /(?<![^\s])#[^\n]*/y],
    ["y", /(?<=^[ \t]*(?:-[ \t]+)?)[A-Za-z_][\w-]*(?=:)/my],
    ["n", /\b\d[\d.]*\b/y],
  ],
  json: [
    ["y", /"[^"\n]*"(?=\s*:)/y],
    ["s", /"(?:[^"\\\n]|\\.)*"/y],
    ["n", /\b(?:true|false|null|\d[\d.]*)\b/y],
  ],
};

/** Who the landing's code is for, and what they asked: picked out in two teammates' colours. */
export const TRAVELLER = "traveller-42";
export const THE_ASK = "My 6am flight got cancelled. Get me on the next one.";
const MARKED =
  /(traveller-42|My 6am flight got cancelled\. Get me on the next one\.)/;

function marked(token: Token): Token[] {
  return token.text
    .split(MARKED)
    .filter((part) => part !== "")
    .map((text) => ({
      ...token,
      text,
      ...(text === TRAVELLER
        ? { mark: "u" as const }
        : text === THE_ASK
          ? { mark: "m" as const }
          : {}),
    }));
}

/** Code as coloured runs, in order; their texts join back to the code exactly. */
export function tokenize(code: string, lang: Lang): Token[] {
  const rules = RULES[lang];
  const out: Token[] = [];
  let plain = "";
  let i = 0;
  const flush = () => {
    if (plain) out.push({ text: plain });
    plain = "";
  };
  scan: while (i < code.length) {
    for (const [colour, re] of rules) {
      re.lastIndex = i;
      const m = re.exec(code);
      if (m && m[0].length) {
        flush();
        out.push({ text: m[0], colour });
        i += m[0].length;
        continue scan;
      }
    }
    plain += code[i++];
  }
  flush();
  return out.flatMap(marked);
}
